import { useCallback, useEffect, useMemo, useState } from 'react'

import type { OnlyFansList } from '@api'
import { useUnit } from 'effector-react'
import { useMutation, useQuery } from 'react-query'

import { $creatorId } from '@/models/creators.ts'
import { useLists } from '@/modules/chatting/hooks/use-lists'
import { listsService } from '@/modules/chatting/service/lists'
import { unescapeHtmlText } from '@/modules/chatting/ui/chat-window/fan-info/utils'
import { handleError } from '@/utils/error'

import type { CollectionsItem } from './CollectionsList/types'
import { followingListUsersOrderOptions } from './consts'
import { sortDefaultLists } from './utils'

export const useCollectionsListData = () => {
  const creatorId = useUnit($creatorId)
  const [allLists, setAllLists] = useState<CollectionsItem[]>([])
  const [tempListsOrder, setTempListsOrder] = useState<
    CollectionsItem[] | null
  >(null)

  const {
    lists,
    isLoading: isListsLoading,
    canLoadMore,
    onLoadMore,
    isFetchingNextPage,
    sort,
    order,
    query,
    setOrder,
    setSort,
    setQuery,
    sortOptions,
    orderOptions,
    isSortAndOrderDisabled,
  } = useLists('all-lists-extended')

  const {
    data: counts,
    isLoading: isCountsLoading,
    refetch: refetchCount,
  } = useQuery(
    'list-items-count',
    () => listsService.getListUsersCount({ creatorId: creatorId! }),
    {
      enabled: !!creatorId,
      onError: (error) => {
        handleError(
          error,
          "Something went wrong while getting list's users' count",
          refetchCount,
        )
      },
    },
  )
  const saveCustomOrderMutation = useMutation(
    (order: OnlyFansList['id'][]) =>
      listsService.saveCustomOrder(creatorId!, order),
    {
      onSuccess: () => {
        setSort('custom')
      },
      onError: (error) => {
        handleError(error, 'Something went wrong while saving custom order')
      },
    },
  )

  const isLoading = isListsLoading || isCountsLoading

  const defaultLists = useMemo<CollectionsItem[]>(() => {
    if (!counts) return []

    const lists: CollectionsItem[] = [
      {
        id: 'restricted',
        name: 'Restricted',
        usersFetcher: listsService.getRestrictedUsers,
        usersCount:
          counts?.subscribers.restricted + counts?.subscriptions.restricted,
      },
      {
        id: 'blocked',
        name: 'Blocked',
        usersFetcher: listsService.getBlockedUsers,
        usersCount: counts?.subscribers.blocked + counts?.subscriptions.blocked,
      },
      {
        id: 'following-all',
        name: 'Following',
        usersFetcher: (options) =>
          listsService.getFollowingUsers({ ...options, type: 'all' }),
        usersCount: counts?.subscriptions.all,
        orderOptions: followingListUsersOrderOptions,
      },
      {
        id: 'following-active',
        name: 'Following active',
        usersFetcher: (options) =>
          listsService.getFollowingUsers({ ...options, type: 'active' }),
        usersCount: counts?.subscriptions.active,
        orderOptions: followingListUsersOrderOptions,
        canSendMessage: true,
      },
      {
        id: 'following-expired',
        name: 'Following expired',
        usersFetcher: (options) =>
          listsService.getFollowingUsers({ ...options, type: 'expired' }),
        usersCount: counts?.subscriptions.expired,
        orderOptions: followingListUsersOrderOptions,
      },
      {
        id: 'fans-all',
        name: 'Fans',
        usersFetcher: (options) =>
          listsService.getSubscribers({ ...options, type: 'all' }),
        usersCount: counts?.subscribers.all,
        orderOptions: null,
      },
      {
        id: 'fans-active',
        name: 'Fans active',
        usersFetcher: (options) =>
          listsService.getSubscribers({ ...options, type: 'active' }),
        usersCount: counts?.subscribers.active,
        orderOptions: null,
      },
      {
        id: 'fans-expired',
        name: 'Fans expired',
        usersFetcher: (options) =>
          listsService.getSubscribers({ ...options, type: 'expired' }),
        usersCount: counts?.subscribers.expired,
        orderOptions: null,
      },
    ]

    const filteredDefaultLists = lists.filter((list) =>
      unescapeHtmlText(list.name).toLowerCase().includes(query.toLowerCase()),
    )

    const sortedDefaultLists = sortDefaultLists(
      filteredDefaultLists,
      sort,
      order,
    )

    return sortedDefaultLists
  }, [counts, query, sort, order])

  const customLists = useMemo<CollectionsItem[]>(() => {
    if (!lists) return []

    return lists.reduce<CollectionsItem[]>((acc, list) => {
      if (list.type === 'custom') {
        acc.push({
          id: list.id,
          name: list.name,
          usersCount: list.usersCount,
          postsCount: list.postsCount,
          type: list.type,
          users: list.users,
        })
      }

      return acc
    }, [])
  }, [lists])

  useEffect(() => {
    if (
      defaultLists.length === 0 &&
      customLists.length === 0 &&
      !isListsLoading
    ) {
      setAllLists([])
    } else {
      setAllLists([...defaultLists, ...customLists])
    }
  }, [defaultLists, customLists, isListsLoading])

  const listsToRender = useMemo(
    () =>
      tempListsOrder
        ? [...tempListsOrder, ...allLists.slice(tempListsOrder.length)]
        : allLists,
    [tempListsOrder, allLists],
  )

  const handleReorder = useCallback(
    (origin: CollectionsItem, target: CollectionsItem) => {
      const newLists = [...listsToRender]
      const originIndex = newLists.indexOf(origin)
      const targetIndex = newLists.indexOf(target)

      newLists[originIndex] = target
      newLists[targetIndex] = origin

      setTempListsOrder((tempListsOrder) =>
        newLists.slice(
          0,
          Math.max(
            originIndex + 1,
            targetIndex + 1,
            tempListsOrder?.length ?? 0,
          ),
        ),
      )
    },
    [listsToRender],
  )

  const cancelOrderChanges = useCallback(() => {
    setTempListsOrder(null)
  }, [])

  const saveCustomOrderChanges = useCallback(async (): Promise<void> => {
    if (tempListsOrder) {
      const order = tempListsOrder
        .filter((list) => list.type === 'custom')
        .map((list) => list.id)

      await saveCustomOrderMutation.mutateAsync(order)
    }

    setTempListsOrder(null)
  }, [tempListsOrder, saveCustomOrderMutation])

  return {
    lists: listsToRender,
    onReorder: handleReorder,
    cancelOrderChanges,
    saveCustomOrderChanges,
    isCustomOrderChangeLoading: saveCustomOrderMutation.isLoading,
    isLoading,
    canLoadMore,
    onLoadMore,
    isFetchingNextPage,
    sort,
    setSort,
    order,
    setOrder,
    query,
    setQuery,
    orderOptions,
    sortOptions,
    isSortAndOrderDisabled,
  }
}
